// The viewport's look-at point, derived from the content that is actually
// loaded — the splat centre for an inference bundle, the rig's bounding-sphere
// centre for a GLB avatar. Whoever loads content publishes it here; the editor
// camera reads it.
//
// A module singleton rather than React state: the publishers are load-time
// callbacks and render-loop code, and the only React consumer wants a value to
// hand to <OrbitControls target>, not a re-render on the hot path.
//
// `null` means nothing has been framed yet, and it is NOT the same as the
// origin: a consumer must leave the camera alone rather than aim it at (0,0,0),
// which is the floor for every character we ship.

let target = null
let framer = null
const subscribers = new Set()

/** The current look-at as [x, y, z], or null when no content has framed yet. */
export function getContentTarget() {
  return target
}

/** Publish the look-at for the content just loaded (or just re-framed). */
export function setContentTarget(x, y, z) {
  if (![x, y, z].every(Number.isFinite)) return false
  // Identity is the change signal for React consumers, so only allocate when
  // the value actually moved — a fresh array every frame would re-apply the
  // target prop and undo the user's pan.
  if (target && target[0] === x && target[1] === y && target[2] === z) return false
  target = [x, y, z]
  for (const fn of subscribers) fn(target)
  return true
}

// Framing the view (moving the camera, not just aiming it) needs the content's
// EXTENT, which only its owner has — the splat pipeline holds per-branch verts,
// the rig holds a scene graph. So the owner reduces its content to a sphere and
// calls fitCameraToSphere; the Frame Avatar / F command delegates through the
// registered framer rather than reaching for one content type's globals.

/**
 * Place the camera so a sphere fills the view, and aim the controls at it.
 *
 * A sphere has no orientation, so the distance holds from every angle — fitting
 * a box's largest extent against the vertical FOV alone frames only the view it
 * was computed for and crops the subject from an oblique one. Both FOV axes are
 * checked because the binding one flips with the viewport's aspect.
 *
 * `preserveDirection` keeps the camera on the arc it is already on and changes
 * only the distance, which is what a re-frame command should do. Loading new
 * content leaves it off and takes the front-on view.
 */
export function fitCameraToSphere(camera, controls, center, radius, { preserveDirection = false, margin = 1.1 } = {}) {
  const [cx, cy, cz] = center
  if (!Number.isFinite(radius) || radius <= 0) return false
  if (![cx, cy, cz].every(Number.isFinite)) return false

  const vFov = (camera.fov * Math.PI) / 180
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * (camera.aspect || 1))
  const dist = Math.max(radius / Math.sin(vFov / 2), radius / Math.sin(hFov / 2)) * margin

  let dx = 0, dy = 0, dz = 1
  if (preserveDirection) {
    const vx = camera.position.x - cx, vy = camera.position.y - cy, vz = camera.position.z - cz
    const len = Math.hypot(vx, vy, vz)
    // A camera sitting exactly on the centre has no direction to keep.
    if (len > 1e-6) { dx = vx / len; dy = vy / len; dz = vz / len }
  }

  setContentTarget(cx, cy, cz)
  camera.position.set(cx + dx * dist, cy + dy * dist, cz + dz * dist)
  camera.near = Math.max(0.001, dist * 0.001)
  camera.far = dist * 1000
  camera.lookAt(cx, cy, cz)
  camera.updateProjectionMatrix()
  if (controls) {
    controls.target.set(cx, cy, cz)
    controls.minDistance = dist * 0.01
    controls.maxDistance = dist * 100
    controls.update()
  }
  return true
}

/** Register how to frame the loaded content. Returns an unregister. */
export function setContentFramer(fn) {
  framer = typeof fn === 'function' ? fn : null
  const registered = framer
  return () => { if (framer === registered) framer = null }
}

/** True when some content has registered a way to be framed. */
export function hasContentFramer() {
  return framer !== null
}

/** Run the registered framer. False when none is registered or it declined. */
export function frameContent() {
  if (!framer) return false
  return framer() !== false
}

/** Forget the framing — a character switch invalidates target and framer alike. */
export function clearContent() {
  framer = null
  if (target === null) return
  target = null
  for (const fn of subscribers) fn(target)
}

/** Subscribe to changes; returns an unsubscribe. */
export function subscribeContentTarget(fn) {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}
